import React, { ReactNode } from 'react'
import { FormTable, FormTableRow } from './FormTable'
import { RegisteredIntegerField, RegisteredNumericField } from '../../../components/fields/NumericField'
import { Stack, Typography } from '@mui/material'
import { ShouldShow, ShouldShowAny } from '../../../components/entities/IssueType'
import { useFormContext } from 'react-hook-form'
import { formatInteger } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import VerifiedIcon from '@mui/icons-material/Verified'
import ErrorIcon from '@mui/icons-material/Error'
import { variableLabel } from './variableLabel'
import { FormIssueEntity } from './issue-form/AdminIssueForm'

function OutcomeFields({
    issue,
    noOverAllotmentChecked,
    noSecondaryOfferingChecked,
    noGuarantorsChecked,
    subscriptionQuota,
    calculatedUnitSubTotal,
    calcUnitSubTotalWithinRange,
}: {
    issue: FormIssueEntity
    noOverAllotmentChecked: boolean
    noSecondaryOfferingChecked: boolean
    noGuarantorsChecked: boolean
    subscriptionQuota: ReactNode
    calculatedUnitSubTotal: number | null
    calcUnitSubTotalWithinRange: boolean | null
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const formMethods = useFormContext()
    const [unitSubWithRights, setUnitSubWithRights] = React.useState(formMethods.getValues('unitSubWithRights'))
    const [unitPctSubWithRights, setUnitPctSubWithRights] = React.useState(
        formMethods.getValues('unitPctSubWithRights'),
    )
    const [unitSubWithoutRights, setUnitSubWithoutRights] = React.useState(
        formMethods.getValues('unitSubWithoutRights'),
    )
    const [unitPctSubWithoutRights, setUnitPctSubWithoutRights] = React.useState(
        formMethods.getValues('unitPctSubWithoutRights'),
    )
    const [unitSubGuarantor, setUnitSubGuarantor] = React.useState(formMethods.getValues('unitSubGuarantor'))
    const [unitPctSubGuarantor, setUnitPctSubGuarantor] = React.useState(formMethods.getValues('unitPctSubGuarantor'))

    const overAllotmentSizeSecondary = formMethods.watch('overAllotmentSizeSecondary')
    const offeredUnits = parseInt(formMethods.watch('offeredUnits')) || 0
    const abbTarget = parseInt(formMethods.watch('abbTarget')) || 0
    const totalOfferedUnits =
        parseInt(formMethods.watch('offeredUnits')) + parseInt(formMethods.watch('offeredUnitsUnlisted'))

    const allowUnitSubTotal = !!offeredUnits || !!abbTarget || issue.type === 'Guarantor fee issue'

    return (
        <FormTable>
            <ShouldShow issueType={issue.type} issueField='unitSubTotal'>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label={variableLabel('unitSubTotal', issue)}>
                        <Stack direction='row' spacing={2}>
                            {allowUnitSubTotal ? (
                                <>
                                    <RegisteredIntegerField
                                        label={variableLabel('unitSubTotal', issue)}
                                        name='unitSubTotal'
                                    />
                                    {['Rights issue', 'IPO'].includes(issue.type) ? (
                                        <Stack justifyContent='center'>
                                            <Typography variant='body2'>Teckningsgrad: {subscriptionQuota}</Typography>
                                            {issue.type !== 'IPO' ? (
                                                <Typography variant='body2'>
                                                    Summering av nedan:{' '}
                                                    {formatInteger(calculatedUnitSubTotal, { locale })}{' '}
                                                    {calcUnitSubTotalWithinRange !== null &&
                                                        calcUnitSubTotalWithinRange && (
                                                            <VerifiedIcon sx={{ fontSize: 14 }} color='success' />
                                                        )}
                                                    {calcUnitSubTotalWithinRange !== null &&
                                                        !calcUnitSubTotalWithinRange && (
                                                            <ErrorIcon fontSize='medium' color='error' />
                                                        )}
                                                </Typography>
                                            ) : null}
                                        </Stack>
                                    ) : null}
                                </>
                            ) : (
                                <Typography>Inga nya aktier</Typography>
                            )}
                        </Stack>
                    </FormTableRow>
                ) : null}
            </ShouldShow>
            <ShouldShowAny issueType={issue.type} issueFields={['unitSubWithRights', 'unitPctSubWithRights']}>
                <FormTableRow label='Units tecknade med rätter'>
                    <Stack direction='row' spacing={2}>
                        <RegisteredIntegerField
                            label='Antal'
                            name='unitSubWithRights'
                            disabled={!!unitPctSubWithRights}
                            onChangeValue={(newVal) => {
                                setUnitSubWithRights(newVal)
                            }}
                        />
                        <RegisteredNumericField
                            label='Procent'
                            name='unitPctSubWithRights'
                            minDecimals={0}
                            maxDecimals={2}
                            percentage={true}
                            disabled={!!unitSubWithRights}
                            onChangeValue={(newVal) => {
                                setUnitPctSubWithRights(newVal)
                            }}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShowAny>
            <ShouldShowAny issueType={issue.type} issueFields={['unitSubWithoutRights', 'unitPctSubWithoutRights']}>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label='Units tecknade utan rätter'>
                        <Stack direction='row' spacing={2}>
                            <RegisteredIntegerField
                                label='Antal'
                                name='unitSubWithoutRights'
                                disabled={!!unitPctSubWithoutRights}
                                onChangeValue={(newVal) => {
                                    setUnitSubWithoutRights(newVal)
                                }}
                            />
                            <RegisteredNumericField
                                label='Procent'
                                name='unitPctSubWithoutRights'
                                minDecimals={0}
                                maxDecimals={2}
                                percentage={true}
                                disabled={!!unitSubWithoutRights}
                                onChangeValue={(newVal) => {
                                    setUnitPctSubWithoutRights(newVal)
                                }}
                            />
                        </Stack>
                    </FormTableRow>
                ) : null}
            </ShouldShowAny>
            <ShouldShowAny issueType={issue.type} issueFields={['unitSubGuarantor', 'unitPctSubGuarantor']}>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label='Units tecknade av garanter'>
                        {!noGuarantorsChecked && (
                            <Stack direction='row' spacing={2}>
                                <RegisteredIntegerField
                                    label='Antal'
                                    name='unitSubGuarantor'
                                    disabled={!!unitPctSubGuarantor}
                                    onChangeValue={(newVal) => {
                                        setUnitSubGuarantor(newVal)
                                    }}
                                />
                                <RegisteredNumericField
                                    label='Procent'
                                    name='unitPctSubGuarantor'
                                    minDecimals={0}
                                    maxDecimals={2}
                                    percentage={true}
                                    disabled={!!unitSubGuarantor}
                                    onChangeValue={(newVal) => {
                                        setUnitPctSubGuarantor(newVal)
                                    }}
                                />
                            </Stack>
                        )}
                        {noGuarantorsChecked && <Typography>Inga garanter</Typography>}
                    </FormTableRow>
                ) : null}
            </ShouldShowAny>

            <ShouldShow issueType={issue.type} issueField='secondarySub'>
                <FormTableRow label='Befintliga aktier som tecknats (secondary)'>
                    {noSecondaryOfferingChecked && <Typography>Inga befintliga aktier erbjöds</Typography>}
                    {!noSecondaryOfferingChecked && (
                        <RegisteredIntegerField
                            label='Antal aktier'
                            helperText='Utöver de som erbjöds i emissionen'
                            name='secondarySub'
                        />
                    )}
                </FormTableRow>
            </ShouldShow>

            <ShouldShow issueType={issue.type} issueField='overAllotmentAllocation'>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label='Övertilldelning'>
                        {noOverAllotmentChecked && <Typography>Ingen övertilldelning</Typography>}
                        {!noOverAllotmentChecked && (
                            <RegisteredIntegerField label='Antal' name='overAllotmentAllocation' />
                        )}
                    </FormTableRow>
                ) : null}
            </ShouldShow>

            <ShouldShow issueType={issue.type} issueField='overAllotmentAllocationSecondary'>
                {!noOverAllotmentChecked ? (
                    <FormTableRow label='Övertilldelning (secondary)'>
                        {overAllotmentSizeSecondary && overAllotmentSizeSecondary !== 0 ? (
                            <RegisteredIntegerField label='Antal' name='overAllotmentAllocationSecondary' />
                        ) : (
                            <Typography>Ingen övertilldelning (secondary)</Typography>
                        )}
                    </FormTableRow>
                ) : null}
            </ShouldShow>
        </FormTable>
    )
}

export default OutcomeFields
